import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import { Capacitor } from '@capacitor/core';
import { useRouter } from 'next/router';
import useViewer from 'hooks/useViewer';
import { useInsertUsagePingMutation } from 'types/generated/client';
import { useGeo } from 'context/GeoContext';
import { getDevViewerTier } from 'utils/client/devViewerOverride';

const UsagePing: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const router = useRouter();
  const viewer = useViewer();
  const userId = viewer.viewer?.uid;
  const isReady = router.isReady;
  const pathname = router.pathname;
  const query = router.query;
  // The one shared geo lookup — no more per-navigation /api/v1/ip fetch. Since the site is
  // proxied through Cloudflare, this is also what makes the recorded country/IP real again
  // rather than the Cloudflare PoP.
  const geo = useGeo();
  const [insert] = useInsertUsagePingMutation();

  React.useEffect(() => {
    // Wait until geo resolves so the row carries the real values; on error we still log the
    // pageview (geo fields blank) rather than drop it. A synthetic development viewer has no
    // account to record against and its read-only data source rejects every mutation.
    if (!userId || !isReady || geo.status === 'loading' || getDevViewerTier()) return;

    const queryString = (window.location.search || '').replace('?', '');
    insert({
      variables: {
        userId,
        pathname,
        queryString,
        city: geo.city ?? '',
        country: geo.country ?? '',
        ip: geo.ip ?? '',
        region: geo.region ?? '',
        platform: Capacitor.getPlatform(),
      },
    }).catch((error) => {
      Sentry.captureException(error);
    });
  }, [
    userId,
    pathname,
    isReady,
    insert,
    query,
    geo.status,
    geo.city,
    geo.country,
    geo.ip,
    geo.region,
  ]);

  return <>{children}</>;
};

export default UsagePing;
